import { parse } from 'date-fns';

/**
 * Parse dates from API responses
 * Handles various date formats including:
 * - ISO 8601 with microseconds (e.g., "2025-09-28T17:31:18.281279")
 * - ISO 8601 with timezone
 * - Space-separated date time (e.g., "2025-09-28 17:31:18")
 */
export const parseDate = (dateString: string): Date => {
  if (!dateString) return new Date();

  try {
    // Normalize fractional seconds to milliseconds (keep 3 digits) and preserve timezone
    if (dateString.includes('T') && dateString.includes('.')) {
      // Example: 2025-09-28T17:31:18.281279Z => 2025-09-28T17:31:18.281Z
      dateString = dateString.replace(/(\.\d{3})\d+/, '$1');
    }

    // If ISO string has no timezone, assume UTC by appending 'Z'
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?$/.test(dateString)) {
      dateString += 'Z';
    }

    // Try to parse as-is first
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date;
    }

    // If that fails and it has a space, try replacing space with T
    if (dateString.includes(' ')) {
      const isoString = dateString.replace(' ', 'T');
      const d2 = new Date(isoString);
      if (!isNaN(d2.getTime())) return d2;
    }

    // Fallback
    return new Date();
  } catch (error) {
    console.error('Error parsing date:', dateString, error);
    return new Date();
  }
};

/**
 * Format a date string for display
 * @param dateString - The date string to format
 * @param formatStr - The format string (default: 'PPP p' for date and time)
 * @returns Formatted date string or 'N/A' if invalid
 */
export const formatDate = (dateString: string | undefined | null, formatStr: string = 'PPP p'): string => {
  if (!dateString) return 'N/A';

  try {
    const date = parseDate(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    // Import format function dynamically to avoid circular dependencies
    const { format } = require('date-fns');
    return format(date, formatStr);
  } catch (error) {
    console.error('Error formatting date:', dateString, error);
    return 'N/A';
  }
};